import { globalStore } from '../utils/globalStore';

/**
 * 路由权限反查器
 *
 * 设计前提：
 * - 唯一权限事实来源 = 后端 `/system/auth/get-permission-info` 响应
 * - 前端 Hub 路由是壳层（/hub/system-manage 等），后端菜单下发的是旧路径
 *   （/profile/basic 等），但 component name 相同 → 用它做稳定桥
 * - 前端不维护权限字符串表，所有 meta.perms 运行时从 menu tree 拿
 */

function findMenuByComponent(list: any[] | undefined, component: string): any | null {
  if (!list) return null;
  for (const m of list) {
    const c = (m.component ?? '').replace(/^\/+/, '');
    if (c === component) return m;
    if (m.children?.length) {
      const found = findMenuByComponent(m.children, component);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Hub 路由 → 后端菜单 component 桥接表
 * 这是**结构事实**（哪个前端壳层页对应哪个后端业务组件），
 * 不涉及权限字符串。后端改 meta.perms 不影响本表。
 */
export const HUB_ROUTE_COMPONENT: Record<string, string> = {
  '/hub/system-manage':                  'pages/manage/BasicInfo',
  '/hub/system-manage/basic':            'pages/manage/BasicInfo',
  '/hub/system-manage/user':             'pages/manage/system/UserInfo',
  '/hub/system-manage/role':             'pages/manage/system/RoleManage',
  '/hub/system-manage/voice':            'pages/manage/system/voiceManage',
  '/hub/system-manage/menu':             'pages/manage/system/MenuManage',
  '/hub/system-manage/log/loginlog':     'pages/manage/system/log/Loginlog',
  '/hub/system-manage/log/operationlog': 'pages/manage/system/log/Operationlog',
};

/**
 * 解析一个 Hub 路由对应的后端权限字符串（meta.perms）。
 * 后端菜单未加载完成、或菜单树里没有这个 component → 返回 undefined。
 */
export function resolvePermissionByPath(path: string): string | undefined {
  const component = HUB_ROUTE_COMPONENT[path];
  if (!component) return undefined;
  const menu = findMenuByComponent(globalStore.getMenuList(), component);
  return menu?.meta?.perms;
}

/**
 * 提取用户的角色 key 列表（兼容字符串数组与 InitService 转换后的对象数组）。
 * 只取稳定标识字段（role_key / code），不用 name（展示名会随文案变）。
 */
export function extractRoleKeys(): string[] {
  const roles = globalStore.getUserInfo()?.roles ?? [];
  return roles
    .map((r: any) => {
      if (typeof r === 'string') return r;
      return r?.role_key || r?.code || '';
    })
    .filter(Boolean)
    .map((s: string) => s.toLowerCase());
}

/** 是否超管（后端 roles 包含 'super_admin'） */
export function isSuperAdmin(): boolean {
  return extractRoleKeys().includes('super_admin');
}